import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Check, Loader2, PlayCircle, RotateCcw } from 'lucide-react';
import FacultyBooking from '@/pages/FacultyBooking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.fnd{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:Inter,system-ui,sans-serif;}
.fnd a{color:inherit;}
.fnd-shell{max-width:1280px;margin:0 auto;padding:48px 32px 96px;}
.fnd-hero{background:#0D1B2A;color:#FAF7F0;padding:56px 32px 48px;margin:-48px -32px 40px;border-bottom:3px solid #C8A84B;}
.fnd-hero-inner{max-width:1280px;margin:0 auto;display:grid;grid-template-columns:2fr 1fr;gap:48px;align-items:end;}
.fnd-eyebrow{font-size:11px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:#C8A84B;margin-bottom:8px;}
.fnd-hero h1{font-family:'Cormorant Display',serif;font-size:48px;font-weight:700;line-height:1.05;letter-spacing:-.02em;margin:0 0 8px;}
.fnd-hero h1 em{font-style:italic;color:#C8A84B;}
.fnd-hero p{margin:0;color:rgba(250,247,240,.7);font-size:14px;line-height:1.6;}
.fnd-progress-card{background:rgba(200,168,75,.08);border:1px solid rgba(200,168,75,.22);padding:24px 26px;}
.fnd-progress-l{font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;}
.fnd-progress-n{font-family:'Cormorant Display',serif;font-size:48px;font-weight:700;color:#FAF7F0;line-height:1;margin:6px 0 14px;}
.fnd-progress-n em{font-style:italic;color:#C8A84B;font-size:.6em;margin-left:6px;}
.fnd-progress-bar{height:5px;background:rgba(255,255,255,.1);border-radius:3px;overflow:hidden;}
.fnd-progress-bar > i{display:block;height:100%;background:linear-gradient(90deg,#C8A84B 0%,#fbe8a3 100%);transition:width .4s cubic-bezier(.16,1,.3,1);}
.fnd-progress-meta{font-size:11px;color:rgba(250,247,240,.55);margin-top:10px;letter-spacing:.04em;}
.fnd-roadmap-h{display:flex;align-items:flex-end;justify-content:space-between;margin:32px 0 24px;flex-wrap:wrap;gap:12px;}
.fnd-roadmap-h h2{font-family:'Cormorant Display',serif;font-size:34px;font-weight:700;line-height:1.05;letter-spacing:-.02em;margin:0;}
.fnd-roadmap-h h2 em{font-style:italic;color:#C8A84B;}
.fnd-mod-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px;}
.fnd-mod{background:#fff;border:1px solid rgba(200,168,75,.18);border-left:3px solid rgba(200,168,75,.32);padding:24px 26px;display:flex;flex-direction:column;gap:14px;transition:border-color .25s,transform .25s,box-shadow .25s;}
.fnd-mod:hover{border-color:rgba(200,168,75,.5);transform:translateY(-2px);box-shadow:0 12px 32px rgba(13,27,42,.06);}
.fnd-mod.completed{border-left-color:#10b981;background:linear-gradient(180deg,#fff 0%,#f3fbf6 100%);}
.fnd-mod-top{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
.fnd-mod-week{font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;}
.fnd-mod-title{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;line-height:1.15;letter-spacing:-.01em;margin:6px 0 4px;}
.fnd-mod-sub{font-style:italic;font-family:'Cormorant Display',serif;font-size:15px;color:#3A4A5C;line-height:1.4;}
.fnd-mod-summary{font-size:14px;color:#3A4A5C;line-height:1.65;}
.fnd-mod-tags{display:flex;flex-wrap:wrap;gap:6px;}
.fnd-mod-tag{font-size:10px;font-weight:600;color:#0D1B2A;background:rgba(200,168,75,.12);padding:4px 10px;border-radius:50px;border:1px solid rgba(200,168,75,.22);}
.fnd-mod-foot{display:flex;align-items:center;justify-content:space-between;gap:8px;padding-top:14px;border-top:1px solid rgba(200,168,75,.14);}
.fnd-mod-faculty{font-size:11px;color:#3A4A5C;}
.fnd-mod-faculty strong{color:#0D1B2A;font-weight:700;}
.fnd-mod-actions{display:flex;gap:8px;}
.fnd-btn{display:inline-flex;align-items:center;gap:6px;font-size:10px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;padding:9px 16px;border-radius:50px;border:1px solid;cursor:pointer;background:transparent;color:#0D1B2A;transition:all .2s;text-decoration:none;}
.fnd-btn.primary{background:#0D1B2A;color:#C8A84B;border-color:#0D1B2A;}
.fnd-btn.primary:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.fnd-btn.line{border-color:rgba(13,27,42,.2);color:#0D1B2A;}
.fnd-btn.line:hover{border-color:#C8A84B;color:#C8A84B;}
.fnd-mod-completed-pill{font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#10b981;display:flex;align-items:center;gap:6px;}
.fnd-deliverable{background:rgba(200,168,75,.08);border:1px dashed rgba(200,168,75,.35);padding:11px 14px;display:flex;align-items:center;gap:10px;}
.fnd-deliverable-l{font-size:9px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#C8A84B;}
.fnd-deliverable-h{font-size:13px;color:#0D1B2A;font-weight:600;}

/* Video player modal */
.fnd-vmodal{position:fixed;inset:0;background:rgba(13,27,42,.92);backdrop-filter:blur(12px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:48px;animation:vmIn .3s ease-out;}
@keyframes vmIn{from{opacity:0;}to{opacity:1;}}
.fnd-vmodal-inner{position:relative;width:100%;max-width:1080px;background:#0D1B2A;border:1px solid rgba(200,168,75,.32);box-shadow:0 32px 80px rgba(0,0,0,.5);}
.fnd-vmodal-head{display:flex;justify-content:space-between;align-items:center;padding:18px 24px;border-bottom:1px solid rgba(200,168,75,.18);gap:18px;flex-wrap:wrap;}
.fnd-vmodal-meta{display:flex;flex-direction:column;gap:4px;color:#FAF7F0;}
.fnd-vmodal-week{font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;}
.fnd-vmodal-title{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#FAF7F0;line-height:1.15;letter-spacing:-.01em;}
.fnd-vmodal-title em{font-style:italic;color:#C8A84B;}
.fnd-vmodal-close{background:transparent;border:1px solid rgba(250,247,240,.3);color:#FAF7F0;font-size:10px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;padding:9px 18px;border-radius:50px;cursor:pointer;transition:all .2s;}
.fnd-vmodal-close:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.fnd-vmodal-video{position:relative;width:100%;aspect-ratio:16/9;background:#000;}
.fnd-vmodal-video video{width:100%;height:100%;object-fit:contain;background:#000;}
.fnd-vmodal-foot{padding:16px 24px;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;border-top:1px solid rgba(200,168,75,.18);}
.fnd-vmodal-faculty{font-size:11px;color:rgba(250,247,240,.7);}
.fnd-vmodal-faculty strong{color:#C8A84B;font-weight:700;}
.fnd-vmodal-actions{display:flex;gap:8px;}
.fnd-vmodal-btn{font-size:10px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;padding:9px 16px;border-radius:50px;cursor:pointer;border:1px solid;transition:all .2s;}
.fnd-vmodal-btn.primary{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.fnd-vmodal-btn.primary:hover{background:#FAF7F0;}
.fnd-vmodal-btn.line{background:transparent;color:#FAF7F0;border-color:rgba(200,168,75,.3);}
.fnd-vmodal-btn.line:hover{border-color:#C8A84B;color:#C8A84B;}
@media(max-width:640px){.fnd-vmodal{padding:16px;}.fnd-vmodal-head{padding:14px 18px;}.fnd-vmodal-foot{padding:14px 18px;}}

@media(max-width:980px){
  .fnd-hero-inner,.fnd-mod-grid{grid-template-columns:1fr;}
  .fnd-hero{padding:36px 24px 32px;margin:-48px -32px 32px;}
  .fnd-shell{padding:48px 24px 80px;}
}
`;

export default function MyCohort() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [videoOpen, setVideoOpen] = useState(null); // null | module obj

  useEffect(() => { document.title = 'My Cohort 1 · SKI AXIS'; }, []);

  // Close video modal on ESC; lock body scroll while open
  useEffect(() => {
    if (!videoOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setVideoOpen(null); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [videoOpen]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/founder/cohort/curriculum`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load your cohort dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchData(); /* eslint-disable-next-line */ }, [token]);

  const toggleComplete = async (mod) => {
    if (busy) return;
    setBusy(mod.id);
    try {
      if (mod.completed) {
        await axios.delete(`${API}/founder/cohort/modules/${mod.id}/complete`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API}/founder/cohort/modules/${mod.id}/complete`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      fetchData();
    } catch (e) {
      alert('Update failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const nextModule = useMemo(() => data?.modules?.find((m) => !m.completed), [data]);

  if (!user) {
    return (
      <div className="fnd"><style>{STYLES}</style><div className="fnd-shell" style={{ textAlign: 'center', padding: 120 }}>
        <h2 style={{ fontFamily: "'Cormorant Display',serif", fontSize: 44 }}>Please sign in</h2>
        <p>Your Cohort 1 dashboard is private.</p>
        <button className="fnd-btn primary" onClick={() => navigate('/login')} data-testid="fnd-signin">Sign in →</button>
      </div></div>
    );
  }

  return (
    <div className="fnd" data-testid="my-cohort-page">
      <style>{STYLES}</style>
      <div className="fnd-shell">
        <section className="fnd-hero" data-testid="fnd-hero">
          <div className="fnd-hero-inner">
            <div>
              <div className="fnd-eyebrow">SKI AXIS · Founder Portal</div>
              <h1>Welcome back, <em>{(user.full_name || user.name || user.email || '').split(' ')[0]}</em>.</h1>
              <p>{data?.cohort?.label || 'Cohort 1 · July 2026'} — your 6-week roadmap from UAE company registration to demo-day pitch.</p>
            </div>
            {data && (
              <div className="fnd-progress-card" data-testid="fnd-progress-card">
                <div className="fnd-progress-l">Programme Progress</div>
                <div className="fnd-progress-n">{data.progress.percent}<em>%</em></div>
                <div className="fnd-progress-bar"><i style={{ width: `${data.progress.percent}%` }} /></div>
                <div className="fnd-progress-meta">
                  {data.progress.completed} of {data.progress.total} modules complete
                  {nextModule && ` · Next: Week ${nextModule.week}`}
                </div>
              </div>
            )}
          </div>
        </section>

        {loading && (
          <div style={{ padding: 60, textAlign: 'center', color: '#7A8190' }}>
            <Loader2 className="inline h-5 w-5 animate-spin mr-2" />Loading your cohort…
          </div>
        )}

        {error && (
          <div style={{ padding: 32, textAlign: 'center', background: '#fff', border: '1px solid rgba(239,68,68,.3)', color: '#ef4444', borderRadius: 4 }}>
            {error}
          </div>
        )}

        {!loading && data && (
          <>
            <div className="fnd-roadmap-h">
              <h2>Cohort <em>Roadmap</em></h2>
              <span style={{ fontSize: 11, color: '#7A8190', letterSpacing: '.04em' }}>
                {data.cohort?.weeks || 6} weeks · {data.cohort?.starts_at || '2026-07-01'}
              </span>
            </div>
            <div className="fnd-mod-grid" data-testid="fnd-modules">
              {data.modules.map((m) => (
                <article
                  key={m.id}
                  className={`fnd-mod ${m.completed ? 'completed' : ''}`}
                  data-testid={`fnd-module-${m.id}`}
                >
                  <div className="fnd-mod-top">
                    <div>
                      <div className="fnd-mod-week">Week {m.week} · {m.duration_min} min</div>
                      <h3 className="fnd-mod-title">{m.title}</h3>
                      <div className="fnd-mod-sub">{m.subtitle}</div>
                    </div>
                    {m.completed && (
                      <div className="fnd-mod-completed-pill" title={m.completed_at ? `Completed ${new Date(m.completed_at).toLocaleDateString()}` : ''}>
                        <Check className="h-3.5 w-3.5" />Complete
                      </div>
                    )}
                  </div>
                  <p className="fnd-mod-summary">{m.summary}</p>
                  <div className="fnd-mod-tags">
                    {(m.topics || []).map((t) => <span key={t} className="fnd-mod-tag">{t}</span>)}
                  </div>
                  <div className="fnd-deliverable">
                    <span className="fnd-deliverable-l">Deliverable</span>
                    <span className="fnd-deliverable-h">{m.deliverable}</span>
                  </div>
                  <div className="fnd-mod-foot">
                    <div className="fnd-mod-faculty"><strong>Faculty:</strong> {m.faculty}</div>
                    <div className="fnd-mod-actions">
                      <button className="fnd-btn line" onClick={() => setVideoOpen(m)} data-testid={`fnd-mod-open-${m.id}`}>
                        <PlayCircle className="h-3 w-3" />Open
                      </button>
                      <button
                        className={`fnd-btn ${m.completed ? 'line' : 'primary'}`}
                        disabled={busy === m.id}
                        onClick={() => toggleComplete(m)}
                        data-testid={`fnd-mod-toggle-${m.id}`}
                      >
                        {busy === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> :
                          m.completed ? <><RotateCcw className="h-3 w-3" />Undo</> : <><Check className="h-3 w-3" />Mark complete</>}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div style={{ marginTop: 48 }} data-testid="fnd-faculty-section">
              <FacultyBooking embedded />
            </div>
          </>
        )}
      </div>

      {/* VIDEO PLAYER MODAL */}
      {videoOpen && (
        <div className="fnd-vmodal" onClick={() => setVideoOpen(null)} data-testid="fnd-vmodal">
          <div className="fnd-vmodal-inner" onClick={(e) => e.stopPropagation()}>
            <div className="fnd-vmodal-head">
              <div className="fnd-vmodal-meta">
                <div className="fnd-vmodal-week">Week {videoOpen.week} · {videoOpen.duration_min} min · Module {videoOpen.id.toUpperCase()}</div>
                <div className="fnd-vmodal-title">{videoOpen.title.split(' ').slice(0, -1).join(' ')} <em>{videoOpen.title.split(' ').slice(-1)[0]}</em></div>
              </div>
              <button className="fnd-vmodal-close" onClick={() => setVideoOpen(null)} data-testid="fnd-vmodal-close">Close · Esc</button>
            </div>
            <div className="fnd-vmodal-video">
              {videoOpen.video_filename ? (
                <video
                  key={videoOpen.id}
                  src={`${API}/video/${videoOpen.video_filename}`}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  data-testid="fnd-vmodal-video-el"
                >
                  Your browser does not support video playback.
                </video>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#FAF7F0', fontStyle: 'italic', fontFamily: "'Cormorant Display',serif", fontSize: 22 }}>
                  Video coming soon — check back closer to launch.
                </div>
              )}
            </div>
            <div className="fnd-vmodal-foot">
              <div className="fnd-vmodal-faculty"><strong>Faculty:</strong> {videoOpen.faculty} · <em>{videoOpen.subtitle}</em></div>
              <div className="fnd-vmodal-actions">
                {!videoOpen.completed && (
                  <button
                    className="fnd-vmodal-btn primary"
                    disabled={busy === videoOpen.id}
                    onClick={async () => { await toggleComplete(videoOpen); setVideoOpen({ ...videoOpen, completed: true }); }}
                    data-testid="fnd-vmodal-mark-complete"
                  >
                    {busy === videoOpen.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Mark Complete →'}
                  </button>
                )}
                <button className="fnd-vmodal-btn line" onClick={() => setVideoOpen(null)} data-testid="fnd-vmodal-back">Back to Roadmap</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
